import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { ROUTES } from '@aibees/elephant/router/routes';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import {
  SEARCH_PARAMS_REHYDRATED_EVENT,
  useSearchParamsStore,
} from '@aibees/elephant/store/search-params-store';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SearchBrowseParams } from '../../types';

declare let window: Window & { __searchParamsStoreRehydrated?: boolean };

const URL_SYNC_DEBOUNCE_MS = 500;

function parseSearchToParams(search: string): Partial<SearchBrowseParams> {
  const entries = new URLSearchParams(search);
  return Object.fromEntries(entries) as Partial<SearchBrowseParams>;
}

/** Deterministic string for params so we can skip setSearchParams when unchanged. */
function paramsToStableString(sanitized: Record<string, string>): string {
  return Object.keys(sanitized)
    .sort()
    .map((k) => `${k}=${sanitized[k]}`)
    .join('&');
}

function pathnameMatchesScope(
  pathname: string,
  scope: SearchTableScope
): boolean {
  if (scope === 'company') return pathname.startsWith(ROUTES.searchCompany);
  if (scope === 'people') return pathname.startsWith(ROUTES.searchPeople);
  if (scope === 'employees')
    return pathname.startsWith(ROUTES.companyEmployees);
  return false;
}

/**
 * Syncs search params store (for current scope) with URL.
 * - Active when scope is 'company', 'people', or 'employees' (SearchLayout or company employees route).
 * - Hydrates store from URL on mount for that scope.
 * - Pushes store → URL (debounced) when store params for that scope change.
 * - Updates store from URL on popstate (back/forward).
 * - Uses per-scope search history store for undo/redo (employees: in-session only, not persisted).
 */
export function SearchParamsUrlSync({
  children,
}: {
  children: React.ReactNode;
}) {
  const scope = useSearchTableScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsByScope = useSearchParamsStore.useParamsByScope();
  const skipHistoryForNextSync =
    useSearchParamsStore.useSkipHistoryForNextSync();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const clearSkipHistoryForNextSync =
    useSearchParamsStore.useClearSkipHistoryForNextSync();
  const addToHistory = useSearchHistoryStore.useAdd();

  const paramsRef = useRef<Partial<SearchBrowseParams>>({});
  const skipRef = useRef(skipHistoryForNextSync);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storeSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSearchParamsRef = useRef(setSearchParams);
  const isHydratingRef = useRef(false);
  const hasRunPostRehydrateRef = useRef(false);
  const prevScopeRef = useRef<SearchTableScope | undefined>(undefined);
  // Tracks what we last pushed to the URL so the URL watcher can
  // distinguish our own syncs from external navigation changes.
  const lastPushedToUrlRef = useRef<Record<string, string>>({});
  const lastPushedStringRef = useRef<string>('');
  // When the effect runs with same URL as last time, the store was updated (we haven't pushed yet) — don't overwrite store.
  const lastUrlParamsRef = useRef<Record<string, string>>({});
  // Stable refs so the URL watcher effect can read current values
  // without listing them as dependencies (avoids firing on store changes).
  const hasScopeRef = useRef(false);
  const hasRehydratedRef = useRef(false);
  const scopeRef = useRef(scope);
  const replaceParamsForScopeRef = useRef(replaceParamsForScope);

  setSearchParamsRef.current = setSearchParams;
  replaceParamsForScopeRef.current = replaceParamsForScope;
  scopeRef.current = scope;

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';
  hasScopeRef.current = hasScope;
  paramsRef.current = hasScope ? paramsByScope[scope] ?? {} : {};
  skipRef.current = skipHistoryForNextSync;

  const scopeParams = hasScope
    ? paramsByScope[scope as SearchTableScope]
    : undefined;

  const [hasRehydrated, setHasRehydrated] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.__searchParamsStoreRehydrated === true
  );
  hasRehydratedRef.current = hasRehydrated;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setHasRehydrated(true);
    window.addEventListener(SEARCH_PARAMS_REHYDRATED_EVENT, handler);
    return () =>
      window.removeEventListener(SEARCH_PARAMS_REHYDRATED_EVENT, handler);
  }, []);

  // Raw store for subscribe/getState so we react to store changes even before rehydration (when useParamsByScope may not trigger re-renders).
  const rawStore = (
    useSearchParamsStore as {
      __store?: {
        subscribe: (listener: () => void) => () => void;
        getState: () => {
          paramsByScope?: Record<SearchTableScope, Partial<SearchBrowseParams>>;
        };
      };
    }
  ).__store;

  // Store subscription: on every store change, debounce and flush current scope params → URL.
  // This fixes the case where the user sets a filter before rehydration: React may not re-render, so the Store→URL effect (which depends on scopeParams) never runs; the subscription always fires when the store updates.
  // Guard: skip until post-rehydrate hydration is complete so stale localStorage params don't overwrite URL.
  useEffect(() => {
    if (!hasScope || scope === 'employees' || !rawStore?.subscribe) return;
    const unsub = rawStore.subscribe(() => {
      // Don't sync to URL until initial hydration from URL is complete
      if (!hasRunPostRehydrateRef.current) return;

      if (storeSyncTimerRef.current) clearTimeout(storeSyncTimerRef.current);
      storeSyncTimerRef.current = setTimeout(() => {
        storeSyncTimerRef.current = null;
        if (
          !pathnameMatchesScope(
            window.location.pathname,
            scope as SearchTableScope
          )
        )
          return;
        const state = rawStore.getState();
        const params = state?.paramsByScope?.[scope as SearchTableScope];
        const nextSanitized = objSanitizeNilValues(params ?? {}) as Record<
          string,
          string
        >;
        const stableString = paramsToStableString(nextSanitized);
        if (stableString === lastPushedStringRef.current) return;
        lastPushedToUrlRef.current = nextSanitized;
        lastPushedStringRef.current = stableString;
        setSearchParamsRef.current(nextSanitized, { replace: true });
      }, URL_SYNC_DEBOUNCE_MS);
    });
    return () => {
      if (storeSyncTimerRef.current) clearTimeout(storeSyncTimerRef.current);
      unsub();
    };
  }, [hasScope, scope, rawStore]);

  // After rehydration: flush store → URL once so URL reflects any in-memory params (e.g. filter set before rehydration completed).
  // Skip flush when URL already has params — the URL is the source of truth in that case (page refresh / external navigation).
  useEffect(() => {
    if (
      !hasRehydrated ||
      !hasScope ||
      scope === 'employees' ||
      !rawStore?.getState
    )
      return;

    // If URL already has params, it is the source of truth — don't overwrite it with (potentially stale) store params.
    const urlParams = parseSearchToParams(window.location.search || '');
    if (Object.keys(urlParams).length > 0) return;

    const state = rawStore.getState();
    const params = state?.paramsByScope?.[scope as SearchTableScope];
    const nextSanitized = objSanitizeNilValues(params ?? {}) as Record<
      string,
      string
    >;
    lastPushedToUrlRef.current = nextSanitized;
    lastPushedStringRef.current = paramsToStableString(nextSanitized);
    setSearchParams(nextSanitized, { replace: true });
  }, [hasRehydrated, hasScope, scope, setSearchParams, rawStore]);

  const runHydrateCurrentScopeFromUrl = useCallback(() => {
    const { search } = window.location;
    const parsed = parseSearchToParams(search || '');
    isHydratingRef.current = true;
    replaceParamsForScope(scope as SearchTableScope, parsed);
    // queueMicrotask fires after all effects in this commit but before
    // React's next re-render, ensuring isHydratingRef is cleared reliably.
    // Unlike setTimeout, it cannot be cancelled by effect cleanup.
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [scope, replaceParamsForScope]);

  // After persist rehydration (once): sync current scope from URL so URL is source of truth (company/people only; employees not persisted).
  // Only hydrate when URL has params or store for this scope is empty, so returning from e.g. saved-search does not overwrite persisted filters with empty URL.
  useEffect(() => {
    if (
      !hasRehydrated ||
      !hasScope ||
      hasRunPostRehydrateRef.current ||
      scope === 'employees'
    )
      return;
    const { search } = window.location;
    const parsed = parseSearchToParams(search || '');
    const urlHasParams = parsed != null && Object.keys(parsed).length > 0;
    const currentScopeParams = paramsByScope[scope as SearchTableScope];
    const storeEmpty =
      !currentScopeParams || Object.keys(currentScopeParams).length === 0;
    if (!urlHasParams && !storeEmpty) {
      hasRunPostRehydrateRef.current = true;
      return;
    }
    hasRunPostRehydrateRef.current = true;
    runHydrateCurrentScopeFromUrl();
  }, [
    hasRehydrated,
    hasScope,
    scope,
    paramsByScope,
    runHydrateCurrentScopeFromUrl,
  ]);

  // When scope is employees: hydrate from URL only when URL has search params.
  // When URL has no params, skip so we don't overwrite programmatic params (e.g. include_current_company from EmployeesTable).
  useEffect(() => {
    if (scope !== 'employees') return;
    const { search } = window.location;
    const parsed = parseSearchToParams(search || '');
    if (Object.keys(parsed).length === 0) return;
    runHydrateCurrentScopeFromUrl();
  }, [scope, runHydrateCurrentScopeFromUrl]);

  // On scope change: flush URL immediately with current scope's params so URL reflects active scope without waiting for debounce.
  // Sets isHydratingRef so the URL→Store watcher (which runs later in this commit) doesn't
  // misinterpret the stale empty searchParams as an external navigation and wipe the store.
  useEffect(() => {
    if (!hasScope) return;
    const prevScope = prevScopeRef.current;
    prevScopeRef.current = scope as SearchTableScope;
    if (prevScope !== undefined && prevScope !== scope) {
      isHydratingRef.current = true;
      const nextSanitized = objSanitizeNilValues(paramsRef.current) as Record<
        string,
        string
      >;
      lastPushedToUrlRef.current = nextSanitized;
      lastPushedStringRef.current = paramsToStableString(nextSanitized);
      setSearchParams(nextSanitized, { replace: true });
      queueMicrotask(() => {
        isHydratingRef.current = false;
      });
    }
  }, [hasScope, scope, setSearchParams]);

  // Store → URL: when params for this scope change
  useEffect(() => {
    if (!hasScope || isHydratingRef.current) return;

    const params = paramsRef.current;
    const sanitized = objSanitizeNilValues(params) as Record<string, string>;

    if (!skipRef.current) {
      addToHistory(scope as SearchTableScope, sanitized);
      clearSkipHistoryForNextSync();
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (
        !pathnameMatchesScope(
          window.location.pathname,
          scope as SearchTableScope
        )
      )
        return;
      const currentParams = paramsRef.current;
      const nextSanitized = objSanitizeNilValues(currentParams) as Record<
        string,
        string
      >;
      const stableString = paramsToStableString(nextSanitized);
      if (stableString === lastPushedStringRef.current) return;
      lastPushedToUrlRef.current = nextSanitized;
      lastPushedStringRef.current = stableString;
      setSearchParams(nextSanitized, { replace: true });
    }, URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    hasScope,
    scope,
    scopeParams,
    setSearchParams,
    addToHistory,
    clearSkipHistoryForNextSync,
  ]);

  const handlePopState = useCallback(() => {
    const { pathname, search } = window.location;

    // Derive scope from the actual URL, not the stale React state (fixed the issue where the scope is not updated when the URL is changed on back button click)
    let currentScope: SearchTableScope | undefined;
    if (pathname.startsWith(ROUTES.searchCompany)) currentScope = 'company';
    else if (pathname.startsWith(ROUTES.searchPeople)) currentScope = 'people';
    else if (pathname.startsWith(ROUTES.companyEmployees))
      currentScope = 'employees';

    // Hydrate the store from the URL
    if (!currentScope) return;
    const parsed = parseSearchToParams(search || '');
    replaceParamsForScope(currentScope, parsed);
  }, [replaceParamsForScope]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  // URL → store watcher: when React Router's searchParams change (from navigation, link click,
  // navigate(), or page refresh), hydrate the store if the URL differs from what we last pushed.
  // Uses only searchParams as a trigger dep; other values are read via refs to avoid firing on store changes.
  useEffect(() => {
    if (
      !hasScopeRef.current ||
      !hasRehydratedRef.current ||
      isHydratingRef.current
    )
      return;

    const urlParams = Object.fromEntries(searchParams.entries()) as Record<
      string,
      string
    >;

    // If URL params are unchanged from last run, this effect re-ran due to re-render (e.g. store
    // updated). Don't overwrite the store — the store is the source of truth until we push.
    if (isEqual(urlParams, lastUrlParamsRef.current)) {
      lastPushedToUrlRef.current = objSanitizeNilValues(
        paramsRef.current
      ) as Record<string, string>;
      return;
    }
    lastUrlParamsRef.current = urlParams;

    // Skip if this URL change was caused by our own store → URL sync
    if (isEqual(urlParams, lastPushedToUrlRef.current)) {
      lastUrlParamsRef.current = urlParams;
      return;
    }

    // Skip if store already has the correct values (e.g. hydration already ran)
    const storeParams = objSanitizeNilValues(paramsRef.current) as Record<
      string,
      string
    >;
    if (isEqual(urlParams, storeParams)) {
      lastPushedToUrlRef.current = urlParams;
      return;
    }

    // URL was changed externally — hydrate the store from URL
    isHydratingRef.current = true;
    replaceParamsForScopeRef.current(
      scopeRef.current as SearchTableScope,
      urlParams as Partial<SearchBrowseParams>
    );
    lastPushedToUrlRef.current = urlParams;
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return <>{children}</>;
}
